import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import api from "../../../api";
import "./ReportingTreeManager.css";

const getId = (item) => String(item?._id || item?.id || "");

const normalize = (value) => String(value || "").toLowerCase().trim();

const formatDate = (value) => {
 if (!value) return "No due date";
 const date = new Date(value);
 if (Number.isNaN(date.getTime())) return "Invalid date";
 return date.toLocaleDateString("en-IN", {
 day: "2-digit",
 month: "short",
 year: "numeric",
 });
};

const getDelayLabel = (task) => {
 const status = normalize(task.status);
 if (task.delayedDays > 0) return `${task.delayedDays}d delayed`;
 if (task.delayedHours > 0) return `${Math.ceil(task.delayedHours)}h delayed`;

 if (!task.plannedEndDate || status === "completed") return "";

 const due = new Date(task.plannedEndDate);
 if (Number.isNaN(due.getTime())) return "";

 const today = new Date();
 due.setHours(0, 0, 0, 0);
 today.setHours(0, 0, 0, 0);

 const overdueDays = Math.floor((today.getTime() - due.getTime()) / 86400000);
 return overdueDays > 0 ? `${overdueDays}d overdue` : "";
};

const getInitials = (name) =>
 String(name || "NA")
 .split(" ")
 .filter(Boolean)
 .slice(0, 2)
 .map((part) => part[0])
 .join("")
 .toUpperCase();

const flattenTree = (nodes, output = []) => {
 nodes.forEach((node) => {
 output.push(node);
 flattenTree(node.children || [], output);
 });
 return output;
};

const getTaskAssigneeId = (task) => {
 if (task?.employeeUserId) return String(task.employeeUserId);
 if (task?.assignedEmployee?.userId?._id) return String(task.assignedEmployee.userId._id);
 if (task?.assignedEmployee?.userId) return String(task.assignedEmployee.userId);
 if (task?.assignedEmployee?._id) return String(task.assignedEmployee._id);
 if (task?.employeeId) return String(task.employeeId);
 if (typeof task?.assignedEmployee === "string") return task.assignedEmployee;
 return "";
};

const ReportingTreeManager = () => {
 const currentUser = useSelector((state) => state.auth?.userDetails || {});
 const [tree, setTree] = useState([]);
 const [users, setUsers] = useState([]);
 const [tasks, setTasks] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [selectedWorkOrder, setSelectedWorkOrder] = useState("");
 const [selectedIds, setSelectedIds] = useState([]);
 const [dragIds, setDragIds] = useState([]);
 const [activeDropId, setActiveDropId] = useState("");
 const [expandedTaskIds, setExpandedTaskIds] = useState([]);
 const [search, setSearch] = useState("");
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);

 const loadData = async () => {
 setLoading(true);
 try {
 const [treeRes, usersRes, tasksRes] = await Promise.all([
 api.get(
 selectedWorkOrder
 ? `/users/reporting-tree?workOrder=${selectedWorkOrder}`
 : "/users/reporting-tree"
 ),
 api.get("/users?limit=1000"),
 api.get(
 selectedWorkOrder
 ? `/tasks?limit=500&workOrder=${selectedWorkOrder}`
 : "/tasks?limit=500"
 ),
 ]);

 setTree(treeRes.data?.data || []);
 setUsers(usersRes.data?.data || []);
 setTasks(tasksRes.data?.data || tasksRes.data?.tasks || tasksRes.data || []);
 } catch (error) {
 toast.error(error.response?.data?.message || "Failed to load reporting tree");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 const loadWorkOrders = async () => {
 try {
 const response = await api.get("/work-orders");
 const data =
 response.data?.data ||
 response.data?.workOrders ||
 response.data ||
 [];
 setWorkOrders(Array.isArray(data) ? data : []);
 } catch (error) {
 toast.error(error.response?.data?.message || "Failed to load work orders");
 }
 };

 loadWorkOrders();
 }, []);

 useEffect(() => {
 loadData();
 }, [selectedWorkOrder]);

 const treeUsers = useMemo(() => flattenTree(tree, []), [tree]);

 const adminRoot = useMemo(() => {
 const currentUserId = getId(currentUser);
 const byCurrentUser =
 treeUsers.find((user) => getId(user) === currentUserId) ||
 treeUsers.find(
 (user) => currentUser?.email && normalize(user.email) === normalize(currentUser.email)
 );

 if (byCurrentUser) return byCurrentUser;

 const firstAdmin = treeUsers.find(
 (user) => normalize(user.role?.name || user.role) === "admin"
 );
 if (firstAdmin) return firstAdmin;

 return {
 _id: currentUserId,
 name: currentUser.name || "Admin",
 email: currentUser.email,
 role: { name: "Admin" },
 children: [],
 };
 }, [currentUser, treeUsers]);

 const assignedIds = useMemo(() => {
 const ids = new Set();
 const rootId = getId(adminRoot);
 const walk = (nodes) => {
 nodes.forEach((node) => {
 const nodeId = getId(node);
 if (nodeId && nodeId !== rootId) ids.add(nodeId);
 walk(node.children || []);
 });
 };
 walk(adminRoot?.children || []);
 return ids;
 }, [adminRoot]);

 const visibleUsers = useMemo(() => {
 const text = normalize(search);
 return users
 .filter((user) => {
 const role = normalize(user.role?.name || user.role);
 return role !== "admin" && role !== "client";
 })
 .filter((user) => {
 if (!text) return true;
 return (
 normalize(user.name).includes(text) ||
 normalize(user.email).includes(text) ||
 normalize(user.role?.name || user.role).includes(text)
 );
 })
 .sort((a, b) => {
 const leftAssigned = assignedIds.has(getId(a)) ? 1 : 0;
 const rightAssigned = assignedIds.has(getId(b)) ? 1 : 0;
 if (leftAssigned !== rightAssigned) return leftAssigned - rightAssigned;
 return String(a.name || "").localeCompare(String(b.name || ""));
 });
 }, [assignedIds, search, users]);

 const selectedUsers = useMemo(
 () => users.filter((user) => selectedIds.includes(getId(user))),
 [selectedIds, users]
 );

 const tasksByUser = useMemo(() => {
 const grouped = new Map();
 tasks.forEach((task) => {
 const userId = getTaskAssigneeId(task);
 if (!userId) return;
 if (!grouped.has(userId)) grouped.set(userId, []);
 grouped.get(userId).push(task);
 });
 return grouped;
 }, [tasks]);

 const toggleTaskList = (userId) => {
 setExpandedTaskIds((current) =>
 current.includes(userId)
 ? current.filter((id) => id !== userId)
 : [...current, userId]
 );
 };

 const toggleSelected = (userId) => {
 setSelectedIds((current) =>
 current.includes(userId)
 ? current.filter((id) => id !== userId)
 : [...current, userId]
 );
 };

 const startDrag = (userId) => {
 const ids = selectedIds.includes(userId) ? selectedIds : [userId];
 setDragIds(ids);
 };

 const assignUsers = async (reportsTo) => {
 const userIds = dragIds.length ? dragIds : selectedIds;
 if (!userIds.length) {
 toast.warning("Select users first");
 return;
 }

 if (!selectedWorkOrder) {
 toast.warning("Select a work order first");
 return;
 }

 if (reportsTo && userIds.includes(reportsTo)) {
 toast.warning("A user cannot report to themselves");
 return;
 }

 if (reportsTo && !reportsTo.match(/^[0-9a-fA-F]{24}$/)) {
 toast.warning("Admin user is not loaded yet");
 return;
 }

 setSaving(true);
 try {
 await api.patch("/users/reporting-manager/bulk", {
 userIds,
 reportsTo,
 workOrder: selectedWorkOrder,
 });
 toast.success("Reporting tree updated");
 setSelectedIds([]);
 setDragIds([]);
 setActiveDropId("");
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.message || "Failed to update reporting tree");
 } finally {
 setSaving(false);
 }
 };

 const unassignUser = async (userId, managerId) => {
 if (!userId) return;

 setSaving(true);
 try {
 await api.patch("/users/reporting-manager/bulk", {
 userIds: [userId],
 removeReportsTo: managerId,
 workOrder: selectedWorkOrder,
 });
 toast.success("User removed from reporting tree");
 setSelectedIds((current) => current.filter((id) => id !== userId));
 setDragIds((current) => current.filter((id) => id !== userId));
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.message || "Failed to remove user");
 } finally {
 setSaving(false);
 }
 };

 const renderUserRow = (user) => {
 const userId = getId(user);
 const selected = selectedIds.includes(userId);
 return (
 <div
 key={userId}
 className={`user-row ${selected ? "selected" : ""}`}
 draggable
 onDragStart={() => startDrag(userId)}
 onClick={() => toggleSelected(userId)}
 >
 <span className="avatar">{getInitials(user.name)}</span>
 <div className="user-copy">
 <div className="user-name">{user.name || "Unnamed"}</div>
 <div className="user-role">
 {user.role?.name || "User"}{assignedIds.has(userId) ? " | assigned" : ""}
 </div>
 </div>
 </div>
 );
 };

 const renderNode = (node, parentId = "") => {
 const nodeId = getId(node);
 const isActive = activeDropId === nodeId;
 const isRootNode = nodeId === getId(adminRoot);
 const nodeTasks = tasksByUser.get(nodeId) || [];
 const openTasks = nodeTasks.filter((task) => normalize(task.status) !== "completed").length;
 const delayedTasks = nodeTasks.filter((task) => normalize(task.status).includes("delay")).length;
 const showTaskList = expandedTaskIds.includes(nodeId);

 return (
 <div
 key={nodeId}
 className={`tree-node ${isActive ? "drop-active" : ""}`}
 onDragOver={(event) => {
 event.preventDefault();
 event.stopPropagation();
 setActiveDropId(nodeId);
 }}
 onDragLeave={(event) => {
 event.stopPropagation();
 setActiveDropId("");
 }}
 onDrop={(event) => {
 event.preventDefault();
 event.stopPropagation();
 assignUsers(nodeId);
 }}
 >
 <div className="tree-node-content">
 <div className="tree-node-top">
 <div className="tree-node-main">
 <span className="avatar">{getInitials(node.name)}</span>
 <div className="user-copy">
 <div className="user-name">{node.name || "Unnamed"}</div>
 <div className="user-role">
 {node.role?.name || "User"} | {(node.children || []).length} direct users
 </div>
 </div>
 </div>
 {!isRootNode && parentId && (
 <button
 type="button"
 className="tree-remove-btn"
 title="Remove from reporting tree"
 aria-label="Remove from reporting tree"
 onClick={(event) => {
 event.stopPropagation();
 unassignUser(nodeId, parentId);
 }}
 disabled={saving}
 >
 <i className="fas fa-unlink" />
 </button>
 )}
 </div>

 <div className="tree-task-summary">
 <span>{nodeTasks.length} tasks</span>
 <span>{openTasks} open</span>
 {delayedTasks > 0 && <span className="danger">{delayedTasks} delayed</span>}
 {nodeTasks.length > 0 && (
 <button
 type="button"
 className="tree-task-toggle"
 onClick={() => toggleTaskList(nodeId)}
 >
 {showTaskList ? "Hide" : "View"} tasks
 </button>
 )}
 </div>

 {showTaskList && (
 <div className="tree-task-list">
 {nodeTasks.map((task) => (
 <div className="tree-task-item" key={getId(task)}>
 <div className="tree-task-title">{task.title || "Untitled Task"}</div>
 <div className="tree-task-meta">
 <span>{task.status || "Unassigned"}</span>
 <span>{task.priority || "Medium"}</span>
 <span>{task.workOrder?.woNo || "Other"}</span>
 <span>Due: {formatDate(task.plannedEndDate)}</span>
 {getDelayLabel(task) && (
 <span className="danger">{getDelayLabel(task)}</span>
 )}
 </div>
 </div>
 ))}
 </div>
 )}

 {node.children?.length > 0 && (
 <div className="tree-children">
 {node.children.map((child) => renderNode(child, nodeId))}
 </div>
 )}
 </div>
 </div>
 );
 };

 return (
 <div>
 <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
 <div>
 <h3 className="mb-1">Reporting Tree</h3>
 <span className="text-muted">Assign users under managers for selected work order scope</span>
 </div>
 <div className="d-flex gap-2">
 <button
 type="button"
 className="btn btn-outline-secondary"
 onClick={() => assignUsers(null)}
 disabled={saving || !selectedIds.length}
 >
 <i className="fas fa-unlink me-2" />
 Unassign
 </button>
 <button type="button" className="btn btn-primary" onClick={loadData} disabled={loading}>
 <i className="fas fa-sync-alt me-2" />
 Refresh
 </button>
 </div>
 </div>

 <div className="reporting-manager">
 <div className="panel">
 <div className="panel-header">
 <div>
 <h5 className="mb-0">Users</h5>
 <small className="text-muted">{selectedUsers.length} selected</small>
 </div>
 </div>
 <div className="panel-body">
 <label className="form-label">Work Order Scope</label>
 <select
 className="form-select mb-3"
 value={selectedWorkOrder}
 onChange={(event) => {
 setSelectedWorkOrder(event.target.value);
 setSelectedIds([]);
 setDragIds([]);
 }}
 >
 <option value="">Select work order</option>
 {workOrders.map((workOrder) => (
 <option key={getId(workOrder)} value={getId(workOrder)}>
 {workOrder.woNo || workOrder.woNumber || getId(workOrder)}{workOrder.label ? ` – ${workOrder.label}` : ""}
 {workOrder.project?.projectName ? ` - ${workOrder.project.projectName}` : ""}
 </option>
 ))}
 </select>
 <input
 className="form-control mb-3"
 placeholder="Search users"
 value={search}
 onChange={(event) => setSearch(event.target.value)}
 />
 <div className="user-list">
 {visibleUsers.length ? visibleUsers.map(renderUserRow) : (
 <div className="empty-state">No users found</div>
 )}
 </div>
 </div>
 </div>

 <div className="panel">
 <div className="panel-header">
 <div>
 <h5 className="mb-0">Reporting Tree</h5>
 <small className="text-muted">
 {selectedWorkOrder
 ? "Drop selected users on Admin or any manager"
 : "Select work order to build tree"}
 </small>
 </div>
 </div>
 <div className="panel-body tree-scroll">
 {loading ? (
 <div className="empty-state">Loading reporting tree...</div>
 ) : adminRoot ? (
 renderNode(adminRoot)
 ) : (
 <div className="empty-state">No reporting data found</div>
 )}
 </div>
 </div>
 </div>
 </div>
 );
};

export default ReportingTreeManager;
